import React, { useEffect, useRef, useState } from 'react';
import ReactSelect, {
  Props as ReactSelectProps,
  components,
} from 'react-select';
import Tooltip from 'components/Tooltip/Tooltip';
import {
  Label,
  Select as SelectStyles,
  ErrorMessage,
  CheckboxWrapper,
  CheckboxLable,
  SingleValue as SingleValueStyles,
} from './SelectStyles';
import { CheckInput } from 'components/form/Check';

const customStyles = ({
  size,
  styles,
  withCheckboxes = false,
  hasError,
}: any) => ({
  ...styles,
  control: (provided: any, state: any) => {
    const borderColor = state.isDisabled ? '#D1D1D1' : '#c3cfd9';
    const bgColor = state.isDisabled ? '#E6E6E6' : '#fff';
    const heightSize: any = { sm: 23, md: 30, lg: 30 };

    return {
      ...provided,
      ...styles?.control,
      height: heightSize[size] || 30,
      minHeight: heightSize[size] || 30,
      border: `1px solid ${hasError ? '#FD0F0F' : borderColor}`,
      boxShadow: hasError
        ? '2px 2px 0px #FFD6D7, -2px 2px 0px #FFD6D7, 2px -2px 0px #FFD6D7, -2px -2px 0px #FFD6D7, inset 0px 2px 2px rgba(0, 0, 0, 0.08)'
        : 'none',
      padding: '0',
      outline: 'none',
      background: bgColor,
      cursor: 'pointer',
      overflow: 'hidden',
      borderRadius: '3px',
    };
  },
  input: (provided: any, state: any) => {
    const fontSize: any = { sm: '12px', md: '14px', lg: '14px' };
    return {
      ...provided,
      ...styles?.input,
      fontSize,
      color: '#1D1E25',
      fontFamily: 'Roboto',
      margin: 0,
      padding: 0,
    };
  },
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    ...styles?.dropdownIndicator,
    padding: '0 13px',
    svg: {
      width: '13px',
      fill: '#8795A2',
    },
  }),
  indicatorSeparator: () => ({
    ...styles?.indicatorSeparator,
    display: 'none',
  }),
  valueContainer: (provided: any, state: any) => ({
    ...styles?.valueContainer,
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: '22px',
  }),
  option: (provided: any, state: any) => {
    const st = withCheckboxes ? { backgroundColor: 'transparent' } : {};

    return {
      ...styles?.option,
      ...provided,
      ...st,
      cursor: 'pointer',
    };
  },
  multiValueRemove: (provided: any, state: any) => {
    return {
      ...styles?.option,
      ...provided,
      ':hover': null,
    };
  },
  menuList: (provided: any) => ({
    ...provided,
    overflowX: 'hidden',
  }),
});

const Option = (props: any) => {
  return (
    <div>
      <components.Option {...props}>
        <CheckboxWrapper>
          <CheckInput register={{ checked: props.isSelected }} />
          <CheckboxLable title={props.label}>{props.label}</CheckboxLable>
        </CheckboxWrapper>
      </components.Option>
    </div>
  );
};

const MultiValue = (props: any) => {
  if (props.index > 0) return null;
  const { length } = props.getValue();
  let label = `${length} selected`;
  if (length === props.options.length) {
    label = 'All';
  }
  return <div>{label}</div>;
};

const SingleValue = (props: any) => {
  const { singleValueTextRef, singleValueRef, getValue } = props;
  const value = getValue();

  if (value[0]) {
    return (
      <SingleValueStyles ref={singleValueRef}>
        <span ref={singleValueTextRef}>{value[0].label}</span>
      </SingleValueStyles>
    );
  }

  return <SingleValueStyles></SingleValueStyles>;
};

type Props = {
  isTree?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  isPortalled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  errorMessage?: string;
  style?: any;
  withCheckboxes?: boolean;
  hasError?: boolean;
  onChange: (e: any, selectedItem?: any) => void;
  ref?: any;
  testid?: string;
  isLoading?: boolean;
} & Omit<ReactSelectProps, 'isDisabled' | 'onChange'>;

export const Select: React.FC<Props> = ({
  isTree = false,
  label,
  required = false,
  disabled = false,
  isPortalled,
  size = 'md',
  errorMessage,
  hasError,
  styles,
  style,
  withCheckboxes,
  ref,
  testid,
  ...otherProps
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const singleValueTextRef = useRef<any>(null);
  const singleValueRef = useRef<any>(null);
  const { value: values }: any = otherProps;
  const value = values && values[0] ? values[0]?.label : values?.label;
  const config = withCheckboxes
    ? {
      components: { Option, MultiValue },
      isMulti: true,
      closeMenuOnSelect: false,
      hideSelectedOptions: false,
      isClearable: false,
    }
    : isTree
      ? {}
      : {
        components: {
          SingleValue: (props: any) => (
            <SingleValue
              {...props}
              singleValueTextRef={singleValueTextRef}
              singleValueRef={singleValueRef}
            />
          ),
        },
      };

  const isShowTooltip = () => {
    const w1 = singleValueTextRef?.current?.offsetWidth;
    const w2 = singleValueRef?.current?.offsetWidth;

    return w1 > w2;
  };

  useEffect(() => {
    if (value) {
      setShowTooltip(isShowTooltip());
    }
  }, [value]);

  const renderSelect = () => {
    return (
      <ReactSelect
        menuPlacement='auto'
        menuPortalTarget={isPortalled ? document.body : null}
        menuPosition='absolute'
        aria-label={testid}
        onMenuOpen={() => setShowTooltip(false)}
        onMenuClose={() => setShowTooltip(isShowTooltip())}
        ref={ref}
        isDisabled={disabled}
        styles={customStyles({
          size,
          styles,
          hasError: hasError || errorMessage ? true : false,
          withCheckboxes,
        })}
        {...otherProps}
        {...config}
      />
    );
  };

  let errorProps: any = {};
  if (testid) {
    errorProps['data-testid'] = `${testid}-error-message`;
  }

  return (
    <SelectStyles style={style} data-testid={testid}>
      {label && (
        <Label>
          {required && <span>*</span>}
          {label}
        </Label>
      )}
      {showTooltip ? (
        <Tooltip
          message={value || null}
          delay={300}
          followCursor={false}
          placement='top'
        >
          {renderSelect()}
        </Tooltip>
      ) : (
        renderSelect()
      )}
      {errorMessage && (
        <ErrorMessage {...errorProps}>{errorMessage}</ErrorMessage>
      )}
    </SelectStyles>
  );
};
